<app-feature-hero
    variant="itsm"
    title="Service Request Management"
    description="Drive seamless team collaboration with Infraon’s service request management solution"
    image="/assets/img/feature-pages/infraon-itsm/service-request/itsm-service-request-management-bg-01.png"
    imageAlt="service request management hero image"
></app-feature-hero>

<section class="container section-spacing">
    <div class="row illustrated-feature p-b-120">
        <div class="col-xl-6 col-lg-6 col-md-12 col-sm-12 col-xs-12 reverse-col">
            <div class="illustrated-feature-container-text-right">
                <h2 class="section-title">
                    Understanding Service Request Management
                </h2>
                <p class="section-paragraph">
                    Service Request Management is a core component of
                    <a routerLink="/itsm-tool"><b><u class="primary-color">IT Service Management
                                (ITSM)</u></b></a>
                    that focuses on handling user requests for various services, such
                    as access to applications, software installations, and other
                    support needs. It helps fulfill predefined services that users
                    regularly need to perform their jobs. So, organizations can ensure
                    that user needs are met promptly and consistently. Efficient
                    handling of service requests helps in reducing wait times and
                    improving user experience. It also allows IT teams to manage
                    workloads better by automating routine tasks and standardizing
                    request fulfillment processes.
                </p>
            </div>
        </div>

        <div class="col-xl-6 col-lg-6 col-md-12 col-sm-12 col-xs-12">
            <img loading="lazy"
                src="/assets/img/feature-pages/infraon-itsm/service-request/service-request-feature-1.webp"
                class="w-100" alt="hardware asset management" />
        </div>
    </div>

    <div class="row illustrated-feature p-t-120 reverse-col">
        <div class="col-xl-6 col-lg-6 col-md-12 col-sm-12 col-xs-12">
            <img loading="lazy"
                src="/assets/img/feature-pages/infraon-itsm/service-request/service-request-feature-2.webp"
                class="w-100" alt="hardware asset management" />
        </div>
        <div class="col-xl-6 col-lg-6 col-md-12 col-sm-12 col-xs-12">
            <div class="illustrated-feature-container-text-left">
                <h2 class="section-title">
                    Infraon’s Service Request Management Solution
                </h2>
                <p class="section-paragraph">
                    Infraon’s service request management solution enables any support
                    team member to interact with users to address concerns. Using
                    AI-driven collaboration, service request can be managed – anytime
                    and anywhere. Infraon’s service request management solution helps
                    lower your IT team's total effort by harnessing AI-based
                    collaborative workflows to eliminate repetitive tasks. It
                    leverages automation to speed up how users interact with customers
                    and keep stakeholders updated.
                </p>
            </div>
        </div>
    </div>
</section>

<section class="section-spacing container">
    <div class="ai-based-itsm case-study blog d-flex align-items-center justify-content-between">
        <div>
            <small data-aos="fade-up" data-aos-delay="100" class="text-white">BLOG</small>
            <h2 data-aos="fade-up" data-aos-delay="300" class="section-title text-start text-white">
                Gen AI and the Art of Managing Service Requests
            </h2>
            <p data-aos="fade-up" data-aos-delay="500" class="section-paragraph text-start text-white">
                IT admins can leverage Gen AI to enhance their service management
                processes. This makes life so much easier for users.
            </p>

            <a href="https://infraon.io/blog/using-gen-ai-to-manage-it-service-requests/"
                class="crmform-button text-white">
                <span style="font-weight: 700; vertical-align: middle;">Read blog</span>
                <svg style="vertical-align: middle;" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path
                        d="M15 3H19C19.5304 3 20.0391 3.21071 20.4142 3.58579C20.7893 3.96086 21 4.46957 21 5V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H15"
                        stroke="#0D51D9" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M10 17L15 12L10 7" stroke="#0D51D9" stroke-width="2" stroke-linecap="round"
                        stroke-linejoin="round" />
                    <path d="M15 12H3" stroke="#0D51D9" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
            </a>
        </div>
        <div>
            <img src="/assets/img/feature-pages/itsm-blog-illustration.webp" class="case-study-illustration m-d-none"
                alt="ITSM Blog illustration" />
        </div>
    </div>
</section>

<section class="container section-spacing">
    <h2 class="section-title text-center">
        Key Features of Infraon’s Service <br />
        Request Management Solution
    </h2>

    <div class="row mx-0 align-items-center section-spacing">
        <div class="col-xl-6 col-lg-6 col-md-12 col-sm-12 col-xs-12">
            <div class="illustrated-feature-container-text-left">
                <h3 class="section-title">Automated Ticket Prioritization</h3>
                <p class="section-paragraph">
                    Introduce an advanced priority matrix into the service request
                    process. Let IT teams quickly assess the importance of requests.
                </p>
            </div>
        </div>
        <div class="col-xl-6 col-lg-6 col-md-12 col-sm-12 col-xs-12">
            <img loading="lazy"
                src="/assets/img/feature-pages/infraon-itsm/service-request/itsm-service-request-management-feature-2-01.png"
                class="w-80" alt="ticket alert" />
        </div>
    </div>

    <div class="row mx-0 align-items-center section-spacing reverse-col">
        <div class="col-xl-6 col-lg-6 col-md-12 col-sm-12 col-xs-12">
            <img loading="lazy"
                src="/assets/img/feature-pages/infraon-itsm/service-request/itsm-service-request-management-feature-3-01.png"
                class="w-80" alt="asset s" />
        </div>
        <div class="col-xl-6 col-lg-6 col-md-12 col-sm-12 col-xs-12">
            <div class="illustrated-feature-container-text-left">
                <h3 class="section-title">Service Catalogue Management</h3>
                <p class="section-paragraph">
                    Equip users with full visibility into all IT services. Leverage
                    custom service request templates containing sections and resource
                    data.
                </p>
            </div>
        </div>
    </div>

    <div class="row mx-0 align-items-center section-spacing">
        <div class="col-xl-6 col-lg-6 col-md-12 col-sm-12 col-xs-12">
            <div class="illustrated Other Features of Infraon’s Service Request Management Solution">
                <h3 class="section-title">Gen AI-Based User Experience</h3>
                <p class="section-paragraph">
                    Enhance user experiences by boosting communication capabilities.
                    Engage with users meaningfully and create a positive impression.
                </p>
            </div>
        </div>
        <div class="col-xl-6 col-lg-6 col-md-12 col-sm-12 col-xs-12">
            <img loading="lazy"
                src="/assets/img/feature-pages/infraon-itsm/service-request/itsm-incident-management-feature-4-01.png"
                class="w-80" alt="workspace management" />
        </div>
    </div>

    <div class="row mx-0 align-items-center section-spacing reverse-col">
        <div class="col-xl-6 col-lg-6 col-md-12 col-sm-12 col-xs-12">
            <img loading="lazy"
                src="/assets/img/feature-pages/infraon-itsm/service-request/itsm-service-request-management-feature-5-01.png"
                class="w-80" alt="dashboard" />
        </div>
        <div class="col-xl-6 col-lg-6 col-md-12 col-sm-12 col-xs-12">
            <div class="illustratedOther Features of Infraon’s Service Request Management Solution">
                <h3 class="section-title">Custom Reporting and Dashboards</h3>
                <p class="section-paragraph">
                    Use AI workflows to simplify the process of request monitoring.
                    Get an intuitive dashboard to spot patterns, manage special
                    assignments, etc.
                </p>
            </div>
        </div>
    </div>

    <div class="row mx-0 align-items-center section-spacing">
        <div class="col-xl-6 col-lg-6 col-md-12 col-sm-12 col-xs-12">
            <div class="Other Features of Infraon’s Service Request Management Solution-left">
                <h3 class="section-title">Reduced Costs</h3>
                <p class="section-paragraph">
                    Infraon’s barcode asset tracking solution helps reduce costs by
                    identifying underutilized assets. Extend the life of assets by
                    scheduling preventive maintenance.
                </p>
            </div>
        </div>
        <div class="col-xl-6 col-lg-6 col-md-12 col-sm-12 col-xs-12">
            <img loading="lazy"
                src="/assets/img/feature-pages/infraon-itsm/service-request/asset-verification-feature-7-01.png"
                class="w-80" alt="Asset verification feature." />
        </div>
    </div>
</section>

<section class="container section-spacing">
    <div class="row mx-0 d-flex align-items-center position-relative">
        <div class="col-xl-6 col-lg-6 col-md-12 col-sm-12 col-xs-12 section-spacing">
            <h2 class="section-title">
                Other Features of Infraon’s Service Request Management Solution
            </h2>

            <div class="row mx-0 px-0">
                <ul class="col-xl-12 col-lg-12 col-md-12 col-sm-12 col-xs-12 px-0">
                    <li class="d-flex align-items-center my-3">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path fill-rule="evenodd" clip-rule="evenodd"
                                d="M20.997 10.9741H21C21.551 10.9741 21.999 11.4201 22 11.9711C22.008 14.6421 20.975 17.1571 19.091 19.0511C17.208 20.9451 14.7 21.9921 12.029 22.0001H12C9.33905 22.0001 6.83605 20.9681 4.94905 19.0911C3.05505 17.2081 2.00805 14.7001 2.00005 12.0291C1.99205 9.35712 3.02505 6.84312 4.90905 4.94912C6.79205 3.05512 9.30005 2.00812 11.971 2.00012C12.766 2.01212 13.576 2.09212 14.352 2.27812C14.888 2.40812 15.219 2.94812 15.089 3.48512C14.96 4.02112 14.417 4.35112 13.883 4.22312C13.262 4.07312 12.603 4.01012 11.977 4.00012C9.84005 4.00612 7.83305 4.84412 6.32705 6.35912C4.82005 7.87412 3.99405 9.88612 4.00005 12.0231C4.00605 14.1601 4.84405 16.1661 6.35905 17.6731C7.86905 19.1741 9.87105 20.0001 12 20.0001H12.023C14.16 19.9941 16.167 19.1561 17.673 17.6411C19.18 16.1251 20.006 14.1141 20 11.9771C19.999 11.4251 20.445 10.9751 20.997 10.9741ZM8.29305 11.293C8.68405 10.902 9.31605 10.902 9.70705 11.293L11.951 13.537L18.248 6.34102C18.612 5.92802 19.243 5.88402 19.659 6.24802C20.074 6.61102 20.116 7.24302 19.752 7.65902L12.752 15.659C12.57 15.867 12.31 15.99 12.033 16H12C11.735 16 11.481 15.895 11.293 15.707L8.29305 12.707C7.90205 12.316 7.90205 11.684 8.29305 11.293Z"
                                fill="#231F20" />
                            <mask id="mask0_2106_28640" style="mask-type: luminance" maskUnits="userSpaceOnUse" x="2"
                                y="2" width="21" height="21">
                                <path fill-rule="evenodd" clip-rule="evenodd"
                                    d="M20.997 10.9741H21C21.551 10.9741 21.999 11.4201 22 11.9711C22.008 14.6421 20.975 17.1571 19.091 19.0511C17.208 20.9451 14.7 21.9921 12.029 22.0001H12C9.33905 22.0001 6.83605 20.9681 4.94905 19.0911C3.05505 17.2081 2.00805 14.7001 2.00005 12.0291C1.99205 9.35712 3.02505 6.84312 4.90905 4.94912C6.79205 3.05512 9.30005 2.00812 11.971 2.00012C12.766 2.01212 13.576 2.09212 14.352 2.27812C14.888 2.40812 15.219 2.94812 15.089 3.48512C14.96 4.02112 14.417 4.35112 13.883 4.22312C13.262 4.07312 12.603 4.01012 11.977 4.00012C9.84005 4.00612 7.83305 4.84412 6.32705 6.35912C4.82005 7.87412 3.99405 9.88612 4.00005 12.0231C4.00605 14.1601 4.84405 16.1661 6.35905 17.6731C7.86905 19.1741 9.87105 20.0001 12 20.0001H12.023C14.16 19.9941 16.167 19.1561 17.673 17.6411C19.18 16.1251 20.006 14.1141 20 11.9771C19.999 11.4251 20.445 10.9751 20.997 10.9741ZM8.29305 11.293C8.68405 10.902 9.31605 10.902 9.70705 11.293L11.951 13.537L18.248 6.34102C18.612 5.92802 19.243 5.88402 19.659 6.24802C20.074 6.61102 20.116 7.24302 19.752 7.65902L12.752 15.659C12.57 15.867 12.31 15.99 12.033 16H12C11.735 16 11.481 15.895 11.293 15.707L8.29305 12.707C7.90205 12.316 7.90205 11.684 8.29305 11.293Z"
                                    fill="white" />
                            </mask>
                            <g mask="url(#mask0_2106_28640)">
                                <rect width="24" height="24" fill="#0D51D9" />
                            </g>
                        </svg>
                        <p class="section-paragraph ms-2 mb-0">
                            Gen AI-Driven Chatbots
                        </p>
                    </li>
                    <li class="d-flex align-items-center my-3">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path fill-rule="evenodd" clip-rule="evenodd"
                                d="M20.997 10.9741H21C21.551 10.9741 21.999 11.4201 22 11.9711C22.008 14.6421 20.975 17.1571 19.091 19.0511C17.208 20.9451 14.7 21.9921 12.029 22.0001H12C9.33905 22.0001 6.83605 20.9681 4.94905 19.0911C3.05505 17.2081 2.00805 14.7001 2.00005 12.0291C1.99205 9.35712 3.02505 6.84312 4.90905 4.94912C6.79205 3.05512 9.30005 2.00812 11.971 2.00012C12.766 2.01212 13.576 2.09212 14.352 2.27812C14.888 2.40812 15.219 2.94812 15.089 3.48512C14.96 4.02112 14.417 4.35112 13.883 4.22312C13.262 4.07312 12.603 4.01012 11.977 4.00012C9.84005 4.00612 7.83305 4.84412 6.32705 6.35912C4.82005 7.87412 3.99405 9.88612 4.00005 12.0231C4.00605 14.1601 4.84405 16.1661 6.35905 17.6731C7.86905 19.1741 9.87105 20.0001 12 20.0001H12.023C14.16 19.9941 16.167 19.1561 17.673 17.6411C19.18 16.1251 20.006 14.1141 20 11.9771C19.999 11.4251 20.445 10.9751 20.997 10.9741ZM8.29305 11.293C8.68405 10.902 9.31605 10.902 9.70705 11.293L11.951 13.537L18.248 6.34102C18.612 5.92802 19.243 5.88402 19.659 6.24802C20.074 6.61102 20.116 7.24302 19.752 7.65902L12.752 15.659C12.57 15.867 12.31 15.99 12.033 16H12C11.735 16 11.481 15.895 11.293 15.707L8.29305 12.707C7.90205 12.316 7.90205 11.684 8.29305 11.293Z"
                                fill="#231F20" />
                            <mask id="mask0_2106_28640" style="mask-type: luminance" maskUnits="userSpaceOnUse" x="2"
                                y="2" width="21" height="21">
                                <path fill-rule="evenodd" clip-rule="evenodd"
                                    d="M20.997 10.9741H21C21.551 10.9741 21.999 11.4201 22 11.9711C22.008 14.6421 20.975 17.1571 19.091 19.0511C17.208 20.9451 14.7 21.9921 12.029 22.0001H12C9.33905 22.0001 6.83605 20.9681 4.94905 19.0911C3.05505 17.2081 2.00805 14.7001 2.00005 12.0291C1.99205 9.35712 3.02505 6.84312 4.90905 4.94912C6.79205 3.05512 9.30005 2.00812 11.971 2.00012C12.766 2.01212 13.576 2.09212 14.352 2.27812C14.888 2.40812 15.219 2.94812 15.089 3.48512C14.96 4.02112 14.417 4.35112 13.883 4.22312C13.262 4.07312 12.603 4.01012 11.977 4.00012C9.84005 4.00612 7.83305 4.84412 6.32705 6.35912C4.82005 7.87412 3.99405 9.88612 4.00005 12.0231C4.00605 14.1601 4.84405 16.1661 6.35905 17.6731C7.86905 19.1741 9.87105 20.0001 12 20.0001H12.023C14.16 19.9941 16.167 19.1561 17.673 17.6411C19.18 16.1251 20.006 14.1141 20 11.9771C19.999 11.4251 20.445 10.9751 20.997 10.9741ZM8.29305 11.293C8.68405 10.902 9.31605 10.902 9.70705 11.293L11.951 13.537L18.248 6.34102C18.612 5.92802 19.243 5.88402 19.659 6.24802C20.074 6.61102 20.116 7.24302 19.752 7.65902L12.752 15.659C12.57 15.867 12.31 15.99 12.033 16H12C11.735 16 11.481 15.895 11.293 15.707L8.29305 12.707C7.90205 12.316 7.90205 11.684 8.29305 11.293Z"
                                    fill="white" />
                            </mask>
                            <g mask="url(#mask0_2106_28640)">
                                <rect width="24" height="24" fill="#0D51D9" />
                            </g>
                        </svg>
                        <p class="section-paragraph ms-2 mb-0">
                            User and Role Management
                        </p>
                    </li>
                    <li class="d-flex align-items-center my-3">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path fill-rule="evenodd" clip-rule="evenodd"
                                d="M20.997 10.9741H21C21.551 10.9741 21.999 11.4201 22 11.9711C22.008 14.6421 20.975 17.1571 19.091 19.0511C17.208 20.9451 14.7 21.9921 12.029 22.0001H12C9.33905 22.0001 6.83605 20.9681 4.94905 19.0911C3.05505 17.2081 2.00805 14.7001 2.00005 12.0291C1.99205 9.35712 3.02505 6.84312 4.90905 4.94912C6.79205 3.05512 9.30005 2.00812 11.971 2.00012C12.766 2.01212 13.576 2.09212 14.352 2.27812C14.888 2.40812 15.219 2.94812 15.089 3.48512C14.96 4.02112 14.417 4.35112 13.883 4.22312C13.262 4.07312 12.603 4.01012 11.977 4.00012C9.84005 4.00612 7.83305 4.84412 6.32705 6.35912C4.82005 7.87412 3.99405 9.88612 4.00005 12.0231C4.00605 14.1601 4.84405 16.1661 6.35905 17.6731C7.86905 19.1741 9.87105 20.0001 12 20.0001H12.023C14.16 19.9941 16.167 19.1561 17.673 17.6411C19.18 16.1251 20.006 14.1141 20 11.9771C19.999 11.4251 20.445 10.9751 20.997 10.9741ZM8.29305 11.293C8.68405 10.902 9.31605 10.902 9.70705 11.293L11.951 13.537L18.248 6.34102C18.612 5.92802 19.243 5.88402 19.659 6.24802C20.074 6.61102 20.116 7.24302 19.752 7.65902L12.752 15.659C12.57 15.867 12.31 15.99 12.033 16H12C11.735 16 11.481 15.895 11.293 15.707L8.29305 12.707C7.90205 12.316 7.90205 11.684 8.29305 11.293Z"
                                fill="#231F20" />
                            <mask id="mask0_2106_28640" style="mask-type: luminance" maskUnits="userSpaceOnUse" x="2"
                                y="2" width="21" height="21">
                                <path fill-rule="evenodd" clip-rule="evenodd"
                                    d="M20.997 10.9741H21C21.551 10.9741 21.999 11.4201 22 11.9711C22.008 14.6421 20.975 17.1571 19.091 19.0511C17.208 20.9451 14.7 21.9921 12.029 22.0001H12C9.33905 22.0001 6.83605 20.9681 4.94905 19.0911C3.05505 17.2081 2.00805 14.7001 2.00005 12.0291C1.99205 9.35712 3.02505 6.84312 4.90905 4.94912C6.79205 3.05512 9.30005 2.00812 11.971 2.00012C12.766 2.01212 13.576 2.09212 14.352 2.27812C14.888 2.40812 15.219 2.94812 15.089 3.48512C14.96 4.02112 14.417 4.35112 13.883 4.22312C13.262 4.07312 12.603 4.01012 11.977 4.00012C9.84005 4.00612 7.83305 4.84412 6.32705 6.35912C4.82005 7.87412 3.99405 9.88612 4.00005 12.0231C4.00605 14.1601 4.84405 16.1661 6.35905 17.6731C7.86905 19.1741 9.87105 20.0001 12 20.0001H12.023C14.16 19.9941 16.167 19.1561 17.673 17.6411C19.18 16.1251 20.006 14.1141 20 11.9771C19.999 11.4251 20.445 10.9751 20.997 10.9741ZM8.29305 11.293C8.68405 10.902 9.31605 10.902 9.70705 11.293L11.951 13.537L18.248 6.34102C18.612 5.92802 19.243 5.88402 19.659 6.24802C20.074 6.61102 20.116 7.24302 19.752 7.65902L12.752 15.659C12.57 15.867 12.31 15.99 12.033 16H12C11.735 16 11.481 15.895 11.293 15.707L8.29305 12.707C7.90205 12.316 7.90205 11.684 8.29305 11.293Z"
                                    fill="white" />
                            </mask>
                            <g mask="url(#mask0_2106_28640)">
                                <rect width="24" height="24" fill="#0D51D9" />
                            </g>
                        </svg>
                        <p class="section-paragraph ms-2 mb-0">
                            Custom Workflow Automation
                        </p>
                    </li>
                    <li class="d-flex align-items-center my-3">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path fill-rule="evenodd" clip-rule="evenodd"
                                d="M20.997 10.9741H21C21.551 10.9741 21.999 11.4201 22 11.9711C22.008 14.6421 20.975 17.1571 19.091 19.0511C17.208 20.9451 14.7 21.9921 12.029 22.0001H12C9.33905 22.0001 6.83605 20.9681 4.94905 19.0911C3.05505 17.2081 2.00805 14.7001 2.00005 12.0291C1.99205 9.35712 3.02505 6.84312 4.90905 4.94912C6.79205 3.05512 9.30005 2.00812 11.971 2.00012C12.766 2.01212 13.576 2.09212 14.352 2.27812C14.888 2.40812 15.219 2.94812 15.089 3.48512C14.96 4.02112 14.417 4.35112 13.883 4.22312C13.262 4.07312 12.603 4.01012 11.977 4.00012C9.84005 4.00612 7.83305 4.84412 6.32705 6.35912C4.82005 7.87412 3.99405 9.88612 4.00005 12.0231C4.00605 14.1601 4.84405 16.1661 6.35905 17.6731C7.86905 19.1741 9.87105 20.0001 12 20.0001H12.023C14.16 19.9941 16.167 19.1561 17.673 17.6411C19.18 16.1251 20.006 14.1141 20 11.9771C19.999 11.4251 20.445 10.9751 20.997 10.9741ZM8.29305 11.293C8.68405 10.902 9.31605 10.902 9.70705 11.293L11.951 13.537L18.248 6.34102C18.612 5.92802 19.243 5.88402 19.659 6.24802C20.074 6.61102 20.116 7.24302 19.752 7.65902L12.752 15.659C12.57 15.867 12.31 15.99 12.033 16H12C11.735 16 11.481 15.895 11.293 15.707L8.29305 12.707C7.90205 12.316 7.90205 11.684 8.29305 11.293Z"
                                fill="#231F20" />
                            <mask id="mask0_2106_28640" style="mask-type: luminance" maskUnits="userSpaceOnUse" x="2"
                                y="2" width="21" height="21">
                                <path fill-rule="evenodd" clip-rule="evenodd"
                                    d="M20.997 10.9741H21C21.551 10.9741 21.999 11.4201 22 11.9711C22.008 14.6421 20.975 17.1571 19.091 19.0511C17.208 20.9451 14.7 21.9921 12.029 22.0001H12C9.33905 22.0001 6.83605 20.9681 4.94905 19.0911C3.05505 17.2081 2.00805 14.7001 2.00005 12.0291C1.99205 9.35712 3.02505 6.84312 4.90905 4.94912C6.79205 3.05512 9.30005 2.00812 11.971 2.00012C12.766 2.01212 13.576 2.09212 14.352 2.27812C14.888 2.40812 15.219 2.94812 15.089 3.48512C14.96 4.02112 14.417 4.35112 13.883 4.22312C13.262 4.07312 12.603 4.01012 11.977 4.00012C9.84005 4.00612 7.83305 4.84412 6.32705 6.35912C4.82005 7.87412 3.99405 9.88612 4.00005 12.0231C4.00605 14.1601 4.84405 16.1661 6.35905 17.6731C7.86905 19.1741 9.87105 20.0001 12 20.0001H12.023C14.16 19.9941 16.167 19.1561 17.673 17.6411C19.18 16.1251 20.006 14.1141 20 11.9771C19.999 11.4251 20.445 10.9751 20.997 10.9741ZM8.29305 11.293C8.68405 10.902 9.31605 10.902 9.70705 11.293L11.951 13.537L18.248 6.34102C18.612 5.92802 19.243 5.88402 19.659 6.24802C20.074 6.61102 20.116 7.24302 19.752 7.65902L12.752 15.659C12.57 15.867 12.31 15.99 12.033 16H12C11.735 16 11.481 15.895 11.293 15.707L8.29305 12.707C7.90205 12.316 7.90205 11.684 8.29305 11.293Z"
                                    fill="white" />
                            </mask>
                            <g mask="url(#mask0_2106_28640)">
                                <rect width="24" height="24" fill="#0D51D9" />
                            </g>
                        </svg>
                        <p class="section-paragraph ms-2 mb-0">Mobile Accessibility</p>
                    </li>
                </ul>
            </div>
            <div class="py-3">
                <a routerLink="/registration?product=Infraon ITSM&plan=essential" class="primary-buttons cta-buttons rounded-full">Get
                    started with free trial</a>
            </div>
        </div>

        <div class="col-xl-6 col-lg-6 col-md-6 col-sm-12 col-xs-12">
            <img src="/assets/img/feature-pages/infraon-itsm/service-request/itsm-change-management-feature-5-01.svg"
                class="float-end m-d-none" alt="Other Service" />
        </div>
    </div>
</section>

<section class="section-spacing container">
    <div class="ai-based-itsm ebook d-flex align-items-center justify-content-between primary-bg position-relative">
        <div>
            <small data-aos="fade-up" data-aos-delay="100">EBOOK</small>
            <h2 data-aos="fade-up" data-aos-delay="300" class="section-title text-start">
                ITSM in Manufacturing: The Checklist <br />
                You Need
            </h2>
            <p data-aos="fade-up" data-aos-delay="500" class="section-paragraph text-start">
                Get actionable steps to tackle current industry-specific challenges
                by using <br />
                modernized ITSM solutions.
            </p>

            <a [routerLink]="['/resources', 'ebook', 'future-of-customer-experience']" class="crmform-button">
                <span class="me-1">Download our e-book</span>
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="19" viewBox="0 0 18 19" fill="none">
                    <path
                        d="M10.125 1.375V9.91797L12.6914 7.35156C13.1133 6.89453 13.8516 6.89453 14.2734 7.35156C14.7305 7.77344 14.7305 8.51172 14.2734 8.93359L9.77344 13.4336C9.35156 13.8906 8.61328 13.8906 8.19141 13.4336L3.69141 8.93359C3.23438 8.51172 3.23438 7.77344 3.69141 7.35156C4.11328 6.89453 4.85156 6.89453 5.27344 7.35156L7.875 9.91797V1.375C7.875 0.777344 8.36719 0.25 9 0.25C9.59766 0.25 10.125 0.777344 10.125 1.375ZM2.25 12.625H5.80078L7.38281 14.2422C8.26172 15.1211 9.70312 15.1211 10.582 14.2422L12.1641 12.625H15.75C16.9805 12.625 18 13.6445 18 14.875V16C18 17.2656 16.9805 18.25 15.75 18.25H2.25C0.984375 18.25 0 17.2656 0 16V14.875C0 13.6445 0.984375 12.625 2.25 12.625ZM15.1875 16.2812C15.6445 16.2812 16.0312 15.9297 16.0312 15.4375C16.0312 14.9805 15.6445 14.5938 15.1875 14.5938C14.6953 14.5938 14.3438 14.9805 14.3438 15.4375C14.3438 15.9297 14.6953 16.2812 15.1875 16.2812Z"
                        fill="#0D51D9" />
                </svg>
            </a>
        </div>
        <img src="/assets/img/feature-pages/asset-management-ebook.webp" class="asset-mmanagement-ebook m-d-none"
            alt="asset mmanagement ebook" />
    </div>
</section>

<section class="container section-spacing features">
    <h2 class="section-title p-b-60 mb-0">
        Implementing an Efficient Service Request <br />
        Management Process with Infraon
    </h2>

    <div class="row mx-0 feature-box">
        <div class="col-xl-4 col-lg-4 col-md-6 col-sm-12 col-xs-12 mb-5">
            <div class="feature-wrapper">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="29" viewBox="0 0 16 29" fill="none">
                    <path
                        d="M15.75 8.375C15.75 12.75 12.1953 16.25 7.875 16.25C3.5 16.25 0 12.75 0 8.375C0 4.05469 3.5 0.5 7.875 0.5C12.1953 0.5 15.75 4.05469 15.75 8.375ZM8.75 4C8.75 3.5625 8.3125 3.125 7.875 3.125C4.92188 3.125 2.625 5.47656 2.625 8.375C2.625 8.86719 3.00781 9.25 3.5 9.25C3.9375 9.25 4.375 8.86719 4.375 8.375C4.375 6.46094 5.90625 4.875 7.875 4.875C8.3125 4.875 8.75 4.49219 8.75 4Z"
                        fill="#084CD4" />
                    <path
                        d="M6 26.75V17.8906C6.54688 17.9453 7.14844 18 7.75 18C8.29688 18 8.89844 17.9453 9.5 17.8906V26.75C9.5 27.7344 8.67969 28.5 7.75 28.5C6.76562 28.5 6 27.7344 6 26.75Z"
                        fill="#A8C0F1" />
                </svg>
                <h3 class="section-title-s text-start pt-3">
                    Streamlined Request Logging and Tracking
                </h3>
                <p class="section-paragraph-s text-start">
                    Users must easily submit service requests so that all requests are
                    captured efficiently. Infraon’s service request management
                    solution helps:
                </p>
                <ul style="list-style: disc !important; padding: 0 17px !important">
                    <li class="section-paragraph-s text-start">
                        Submit requests via a portal
                    </li>
                    <li class="section-paragraph-s text-start">
                        Automatically log requests
                    </li>
                    <li class="section-paragraph-s text-start">
                        Track status in real time
                    </li>
                    <li class="section-paragraph-s text-start">
                        Centralize the request database
                    </li>
                </ul>
            </div>
        </div>
        <div class="col-xl-4 col-lg-4 col-md-6 col-sm-12 col-xs-12">
            <div class="feature-wrapper">
                <svg xmlns="http://www.w3.org/2000/svg" width="35" height="28" viewBox="0 0 35 28" fill="none">
                    <path d="M3.5 12.75V23.25H0V12.75H3.5ZM35 12.75V23.25H31.5V12.75H35Z" fill="#A8C1F1" />
                    <path
                        d="M19 0V1.75V5.25H29.5V28H5V5.25H15.5V1.75V0H19ZM14.1875 14C14.1875 12.7969 13.2031 11.8125 12 11.8125C10.7422 11.8125 9.8125 12.7969 9.8125 14C9.8125 15.2578 10.7422 16.1875 12 16.1875C13.2031 16.1875 14.1875 15.2578 14.1875 14ZM22.5 16.1875C23.7031 16.1875 24.6875 15.2578 24.6875 14C24.6875 12.7969 23.7031 11.8125 22.5 11.8125C21.2422 11.8125 20.3125 12.7969 20.3125 14C20.3125 15.2578 21.2422 16.1875 22.5 16.1875Z"
                        fill="#084CD4" />
                    <rect x="10" y="21" width="14" height="2" rx="1" fill="#A8C0F1" />
                </svg>
                <h3 class="section-title-s text-start pt-3">
                    Automated Workflows and Task Assignments
                </h3>
                <p class="section-paragraph-s text-start">
                    Automation ensures that requests are routed to the right teams or
                    individuals. Infraon’s service request management solution allows
                    users to:
                </p>
                <ul style="list-style: disc !important; padding: 0 17px !important">
                    <li class="section-paragraph-s text-start">
                        Use predefined workflow templates
                    </li>
                    <li class="section-paragraph-s text-start">
                        Automate routing based on request type
                    </li>
                    <li class="section-paragraph-s text-start">
                        Use escalation rules for overdue requests
                    </li>
                    <li class="section-paragraph-s text-start">
                        Integrate with other ITSM processes
                    </li>
                </ul>
            </div>
        </div>
        <div class="col-xl-4 col-lg-4 col-md-6 col-sm-12 col-xs-12">
            <div class="feature-wrapper">
                <svg xmlns="http://www.w3.org/2000/svg" width="25" height="29" viewBox="0 0 25 29" fill="none">
                    <path
                        d="M12.5 14C8.61719 14 5.5 10.8828 5.5 7C5.5 3.17188 8.61719 0 12.5 0C16.3281 0 19.5 3.17188 19.5 7C19.5 10.8828 16.3281 14 12.5 14Z"
                        fill="#084CD4" />
                    <path
                        d="M23.7036 26.2868C24.0994 27.5729 23.1377 28.875 21.7921 28.875H2.70792C1.36231 28.875 0.400636 27.5729 0.796361 26.2868L3.06559 18.9118C3.3238 18.0726 4.09914 17.5 4.97715 17.5H19.5229C20.4009 17.5 21.1762 18.0726 21.4344 18.9118L23.7036 26.2868Z"
                        fill="#A8C1F1" />
                </svg>
                <h3 class="section-title-s text-start pt-3">
                    Advanced User Communication
                </h3>
                <p class="section-paragraph-s text-start">
                    It is crucial to keep users informed throughout the request
                    lifecycle. Infraon’s service request management solution helps:
                </p>
                <ul style="list-style: disc !important; padding: 0 17px !important">
                    <li class="section-paragraph-s text-start">
                        Automate acknowledgments
                    </li>
                    <li class="section-paragraph-s text-start">
                        Get updates on request status
                    </li>
                    <li class="section-paragraph-s text-start">
                        Use clear communication channels
                    </li>
                    <li class="section-paragraph-s text-start">
                        Collect feedback after fulfillment
                    </li>
                </ul>
            </div>
        </div>
        <div class="col-xl-4 col-lg-4 col-md-6 col-sm-12 col-xs-12">
            <div class="feature-wrapper">
                <svg xmlns="http://www.w3.org/2000/svg" width="28" height="27" viewBox="0 0 28 27" fill="none">
                    <path
                        d="M0 22.25C0 21.5391 0.546875 20.9375 1.3125 20.9375H4.53906C5.08594 19.1875 6.78125 17.875 8.75 17.875C10.6641 17.875 12.3594 19.1875 12.9062 20.9375H26.6875C27.3984 20.9375 28 21.5391 28 22.25C28 23.0156 27.3984 23.5625 26.6875 23.5625H12.9062C12.3594 25.3672 10.6641 26.625 8.75 26.625C6.78125 26.625 5.08594 25.3672 4.53906 23.5625H1.3125C0.546875 23.5625 0 23.0156 0 22.25ZM5.5 22C5.5 21.0703 9.67969 21.6931 8.75 21.6931C7.76562 21.6931 11.3198 21.3203 11.3198 22.25C11.3198 23.2344 7.76562 22.25 8.75 22.25C9.67969 22.25 5.5 22.9844 5.5 22ZM26.6875 3.4375C27.3984 3.4375 28 4.03906 28 4.75C28 5.51562 27.3984 6.0625 26.6875 6.0625H14.6562C14.1094 7.86719 12.4141 9.125 10.5 9.125C8.53125 9.125 6.83594 7.86719 6.28906 6.0625H1.3125C0.546875 6.0625 0 5.51562 0 4.75C0 4.03906 0.546875 3.4375 1.3125 3.4375H6.28906C6.83594 1.6875 8.53125 0.375 10.5 0.375C12.4141 0.375 14.1094 1.6875 14.6562 3.4375H26.6875ZM12.6992 4.75C12.6992 5.73438 9.41406 4.75 10.3984 4.75C11.3281 4.75 12.25 5.73438 12.25 4.75C12.25 3.82031 11.3281 4.75 10.3984 4.75C9.41406 4.75 12.6992 3.82031 12.6992 4.75Z"
                        fill="#084CD4" />
                    <path
                        d="M19.25 9C21.1641 9 22.8594 10.3125 23.4062 12.0625H26.6875C27.3984 12.0625 28 12.6641 28 13.375C28 14.1406 27.3984 14.6875 26.6875 14.6875H23.4062C22.8594 16.4922 21.1641 17.75 19.25 17.75C17.2812 17.75 15.5859 16.4922 15.0391 14.6875H1.3125C0.546875 14.6875 0 14.1406 0 13.375C0 12.6641 0.546875 12.0625 1.3125 12.0625H15.0391C15.5859 10.3125 17.2812 9 19.25 9ZM21 13C21 12.0703 22.4297 13 21.5 13C20.5156 13 23 12.5703 23 13.5C23 14.4844 16.5156 13 17.5 13C18.4297 13 21 13.9844 21 13Z"
                        fill="#AC67F0" />
                </svg>
                <h3 class="section-title-s text-start pt-3">
                    Integration with IT Asset Management
                </h3>
                <p class="section-paragraph-s text-start">
                    Smooth integration ensures that service requests are handled with
                    visibility into assets. Infraon’s service request management
                    solution lets users:
                </p>
                <ul style="list-style: disc !important; padding: 0 17px !important">
                    <li class="section-paragraph-s text-start">
                        Track assets in real time
                    </li>
                    <li class="section-paragraph-s text-start">
                        Link service requests to assets
                    </li>
                    <li class="section-paragraph-s text-start">
                        Allocate assets during fulfillment
                    </li>
                    <li class="section-paragraph-s text-start">
                        Improve lifecycle management
                    </li>
                </ul>
            </div>
        </div>
    </div>
</section>

<section class="section-spacing container">
    <div class="ai-based-itsm case-study d-flex align-items-center justify-content-between bg-grey"
        style="background-image: none">
        <div>
            <small data-aos="fade-up" data-aos-delay="100">CASE STUDY</small>
            <h2 data-aos="fade-up" data-aos-delay="300" class="section-title text-start">
                How Infraon ITSM Helped Transform a Leading IT Security Organization
            </h2>
            <div class="case-study-pointers">
                <div>
                    <span></span>
                    <span>Agent productivity increased by up to 50%</span>
                </div>
                <div>
                    <span></span>
                    <span>Total IT costs reduced by 25%</span>
                </div>
                <div>
                    <span></span>
                    <span>Customer satisfaction improved by up to 75%</span>
                </div>
            </div>

            <a [routerLink]="['/resources', 'case-studies', 'leader-it']" class="crmform-button">
                <span style="color: #0d51d9 !important; font-weight: 700">Download Case-study</span>
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="19" viewBox="0 0 18 19" fill="none">
                    <path
                        d="M10.125 1.375V9.91797L12.6914 7.35156C13.1133 6.89453 13.8516 6.89453 14.2734 7.35156C14.7305 7.77344 14.7305 8.51172 14.2734 8.93359L9.77344 13.4336C9.35156 13.8906 8.61328 13.8906 8.19141 13.4336L3.69141 8.93359C3.23438 8.51172 3.23438 7.77344 3.69141 7.35156C4.11328 6.89453 4.85156 6.89453 5.27344 7.35156L7.875 9.91797V1.375C7.875 0.777344 8.36719 0.25 9 0.25C9.59766 0.25 10.125 0.777344 10.125 1.375ZM2.25 12.625H5.80078L7.38281 14.2422C8.26172 15.1211 9.70312 15.1211 10.582 14.2422L12.1641 12.625H15.75C16.9805 12.625 18 13.6445 18 14.875V16C18 17.2656 16.9805 18.25 15.75 18.25H2.25C0.984375 18.25 0 17.2656 0 16V14.875C0 13.6445 0.984375 12.625 2.25 12.625ZM15.1875 16.2812C15.6445 16.2812 16.0312 15.9297 16.0312 15.4375C16.0312 14.9805 15.6445 14.5938 15.1875 14.5938C14.6953 14.5938 14.3438 14.9805 14.3438 15.4375C14.3438 15.9297 14.6953 16.2812 15.1875 16.2812Z"
                        fill="#1A5AD9" />
                </svg>
            </a>
        </div>
        <div>
            <img src="/assets/img/feature-pages/case-study-illustration.webp" class="case-study-illustration m-d-none"
                alt="Case Study Illurtation" />
        </div>
    </div>
</section>

<section class="container section-spacing">
    <div class="row align-items-center reverse-col">
        <div class="col-xl-6 col-lg-6 col-md-12 col-sm-12 col-xs-12">
            <img src="/assets/img/feature-pages/infraon-itsm/service-request/service-request-feature-3.webp"
                class="w-100" style="box-shadow: none" alt="it asset management software" />
        </div>

        <div class="col-xl-6 col-lg-6 col-md-12 col-sm-12 col-xs-12">
            <div class="illustrated-feature-container-text-left">
                <h2 class="section-title">
                    Best Practices for Service Request Management
                </h2>
                <p class="section-paragraph">
                    Infraon's service request management solution is led by best
                    practices that include:
                </p>

                <div class="d-flex align-items-start mb-3" style="gap: 8px">
                    <svg style="width: 16px; min-width: 16px" xmlns="http://www.w3.org/2000/svg" width="21" height="22"
                        viewBox="0 0 21 22" fill="none">
                        <path
                            d="M9.63867 14.2422C9.39258 14.4883 8.94141 14.4883 8.69531 14.2422L6.07031 11.6172C5.82422 11.3711 5.82422 10.9199 6.07031 10.6738C6.31641 10.4277 6.76758 10.4277 7.01367 10.6738L9.1875 12.8477L13.9453 8.04883C14.1914 7.80273 14.6426 7.80273 14.8887 8.04883C15.1348 8.29492 15.1348 8.74609 14.8887 8.99219L9.63867 14.2422ZM21 11.125C21 16.9492 16.2832 21.625 10.5 21.625C4.67578 21.625 0 16.9492 0 11.125C0 5.3418 4.67578 0.625 10.5 0.625C16.2832 0.625 21 5.3418 21 11.125ZM10.5 1.9375C5.41406 1.9375 1.3125 6.08008 1.3125 11.125C1.3125 16.2109 5.41406 20.3125 10.5 20.3125C15.5449 20.3125 19.6875 16.2109 19.6875 11.125C19.6875 6.08008 15.5449 1.9375 10.5 1.9375Z"
                            fill="#4B4B4B" />
                    </svg>
                    <p class="section-paragraph-s text-start mb-0">
                        Centralized request submission with a single, user-friendly
                        portal
                    </p>
                </div>
                <div class="d-flex align-items-start mb-3" style="gap: 8px">
                    <svg style="width: 16px; min-width: 16px" xmlns="http://www.w3.org/2000/svg" width="21" height="22"
                        viewBox="0 0 21 22" fill="none">
                        <path
                            d="M9.63867 14.2422C9.39258 14.4883 8.94141 14.4883 8.69531 14.2422L6.07031 11.6172C5.82422 11.3711 5.82422 10.9199 6.07031 10.6738C6.31641 10.4277 6.76758 10.4277 7.01367 10.6738L9.1875 12.8477L13.9453 8.04883C14.1914 7.80273 14.6426 7.80273 14.8887 8.04883C15.1348 8.29492 15.1348 8.74609 14.8887 8.99219L9.63867 14.2422ZM21 11.125C21 16.9492 16.2832 21.625 10.5 21.625C4.67578 21.625 0 16.9492 0 11.125C0 5.3418 4.67578 0.625 10.5 0.625C16.2832 0.625 21 5.3418 21 11.125ZM10.5 1.9375C5.41406 1.9375 1.3125 6.08008 1.3125 11.125C1.3125 16.2109 5.41406 20.3125 10.5 20.3125C15.5449 20.3125 19.6875 16.2109 19.6875 11.125C19.6875 6.08008 15.5449 1.9375 10.5 1.9375Z"
                            fill="#4B4B4B" />
                    </svg>
                    <p class="section-paragraph-s text-start mb-0">
                        Standardized forms and templates to capture all necessary
                        information upfront
                    </p>
                </div>
                <div class="d-flex align-items-start mb-3" style="gap: 8px">
                    <svg style="width: 16px; min-width: 16px" xmlns="http://www.w3.org/2000/svg" width="21" height="22"
                        viewBox="0 0 21 22" fill="none">
                        <path
                            d="M9.63867 14.2422C9.39258 14.4883 8.94141 14.4883 8.69531 14.2422L6.07031 11.6172C5.82422 11.3711 5.82422 10.9199 6.07031 10.6738C6.31641 10.4277 6.76758 10.4277 7.01367 10.6738L9.1875 12.8477L13.9453 8.04883C14.1914 7.80273 14.6426 7.80273 14.8887 8.04883C15.1348 8.29492 15.1348 8.74609 14.8887 8.99219L9.63867 14.2422ZM21 11.125C21 16.9492 16.2832 21.625 10.5 21.625C4.67578 21.625 0 16.9492 0 11.125C0 5.3418 4.67578 0.625 10.5 0.625C16.2832 0.625 21 5.3418 21 11.125ZM10.5 1.9375C5.41406 1.9375 1.3125 6.08008 1.3125 11.125C1.3125 16.2109 5.41406 20.3125 10.5 20.3125C15.5449 20.3125 19.6875 16.2109 19.6875 11.125C19.6875 6.08008 15.5449 1.9375 10.5 1.9375Z"
                            fill="#4B4B4B" />
                    </svg>
                    <p class="section-paragraph-s text-start mb-0">
                        Automated workflows to route requests to the appropriate
                        touch-points
                    </p>
                </div>
                <div class="d-flex align-items-start mb-3" style="gap: 8px">
                    <svg style="width: 16px; min-width: 16px" xmlns="http://www.w3.org/2000/svg" width="21" height="22"
                        viewBox="0 0 21 22" fill="none">
                        <path
                            d="M9.63867 14.2422C9.39258 14.4883 8.94141 14.4883 8.69531 14.2422L6.07031 11.6172C5.82422 11.3711 5.82422 10.9199 6.07031 10.6738C6.31641 10.4277 6.76758 10.4277 7.01367 10.6738L9.1875 12.8477L13.9453 8.04883C14.1914 7.80273 14.6426 7.80273 14.8887 8.04883C15.1348 8.29492 15.1348 8.74609 14.8887 8.99219L9.63867 14.2422ZM21 11.125C21 16.9492 16.2832 21.625 10.5 21.625C4.67578 21.625 0 16.9492 0 11.125C0 5.3418 4.67578 0.625 10.5 0.625C16.2832 0.625 21 5.3418 21 11.125ZM10.5 1.9375C5.41406 1.9375 1.3125 6.08008 1.3125 11.125C1.3125 16.2109 5.41406 20.3125 10.5 20.3125C15.5449 20.3125 19.6875 16.2109 19.6875 11.125C19.6875 6.08008 15.5449 1.9375 10.5 1.9375Z"
                            fill="#4B4B4B" />
                    </svg>
                    <p class="section-paragraph-s text-start mb-0">
                        Clearly defined rules for prioritizing service requests based on
                        urgency
                    </p>
                </div>
                <div class="d-flex align-items-start mb-3" style="gap: 8px">
                    <svg style="width: 16px; min-width: 16px" xmlns="http://www.w3.org/2000/svg" width="21" height="22"
                        viewBox="0 0 21 22" fill="none">
                        <path
                            d="M9.63867 14.2422C9.39258 14.4883 8.94141 14.4883 8.69531 14.2422L6.07031 11.6172C5.82422 11.3711 5.82422 10.9199 6.07031 10.6738C6.31641 10.4277 6.76758 10.4277 7.01367 10.6738L9.1875 12.8477L13.9453 8.04883C14.1914 7.80273 14.6426 7.80273 14.8887 8.04883C15.1348 8.29492 15.1348 8.74609 14.8887 8.99219L9.63867 14.2422ZM21 11.125C21 16.9492 16.2832 21.625 10.5 21.625C4.67578 21.625 0 16.9492 0 11.125C0 5.3418 4.67578 0.625 10.5 0.625C16.2832 0.625 21 5.3418 21 11.125ZM10.5 1.9375C5.41406 1.9375 1.3125 6.08008 1.3125 11.125C1.3125 16.2109 5.41406 20.3125 10.5 20.3125C15.5449 20.3125 19.6875 16.2109 19.6875 11.125C19.6875 6.08008 15.5449 1.9375 10.5 1.9375Z"
                            fill="#4B4B4B" />
                    </svg>
                    <p class="section-paragraph-s text-start mb-0">
                        Continuous monitoring of request metrics and performance data
                        analysis
                    </p>
                </div>
                <div class="d-flex align-items-start mb-3" style="gap: 8px">
                    <svg style="width: 16px; min-width: 16px" xmlns="http://www.w3.org/2000/svg" width="21" height="22"
                        viewBox="0 0 21 22" fill="none">
                        <path
                            d="M9.63867 14.2422C9.39258 14.4883 8.94141 14.4883 8.69531 14.2422L6.07031 11.6172C5.82422 11.3711 5.82422 10.9199 6.07031 10.6738C6.31641 10.4277 6.76758 10.4277 7.01367 10.6738L9.1875 12.8477L13.9453 8.04883C14.1914 7.80273 14.6426 7.80273 14.8887 8.04883C15.1348 8.29492 15.1348 8.74609 14.8887 8.99219L9.63867 14.2422ZM21 11.125C21 16.9492 16.2832 21.625 10.5 21.625C4.67578 21.625 0 16.9492 0 11.125C0 5.3418 4.67578 0.625 10.5 0.625C16.2832 0.625 21 5.3418 21 11.125ZM10.5 1.9375C5.41406 1.9375 1.3125 6.08008 1.3125 11.125C1.3125 16.2109 5.41406 20.3125 10.5 20.3125C15.5449 20.3125 19.6875 16.2109 19.6875 11.125C19.6875 6.08008 15.5449 1.9375 10.5 1.9375Z"
                            fill="#4B4B4B" />
                    </svg>
                    <p class="section-paragraph-s text-start mb-0">
                        Feedback collection about user experience to improve
                        satisfaction levels
                    </p>
                </div>
            </div>
        </div>
    </div>
</section>

<section class="container section-spacing">
    <div class="row align-items-center">
        <div class="col-xl-6 col-lg-6 col-md-12 col-sm-12 col-xs-12">
            <div class="illustrated-feature-container-text-left">
                <h2 class="section-title">
                    Benefits of Service Request Management
                </h2>
                <p class="section-paragraph">
                    Infraon's service request management solution makes it easy to
                    achieve outcomes such as:
                </p>

                <div class="d-flex align-items-start mb-3" style="gap: 8px">
                    <svg style="width: 16px; min-width: 16px" xmlns="http://www.w3.org/2000/svg" width="21" height="22"
                        viewBox="0 0 21 22" fill="none">
                        <path
                            d="M9.63867 14.2422C9.39258 14.4883 8.94141 14.4883 8.69531 14.2422L6.07031 11.6172C5.82422 11.3711 5.82422 10.9199 6.07031 10.6738C6.31641 10.4277 6.76758 10.4277 7.01367 10.6738L9.1875 12.8477L13.9453 8.04883C14.1914 7.80273 14.6426 7.80273 14.8887 8.04883C15.1348 8.29492 15.1348 8.74609 14.8887 8.99219L9.63867 14.2422ZM21 11.125C21 16.9492 16.2832 21.625 10.5 21.625C4.67578 21.625 0 16.9492 0 11.125C0 5.3418 4.67578 0.625 10.5 0.625C16.2832 0.625 21 5.3418 21 11.125ZM10.5 1.9375C5.41406 1.9375 1.3125 6.08008 1.3125 11.125C1.3125 16.2109 5.41406 20.3125 10.5 20.3125C15.5449 20.3125 19.6875 16.2109 19.6875 11.125C19.6875 6.08008 15.5449 1.9375 10.5 1.9375Z"
                            fill="#4B4B4B" />
                    </svg>
                    <p class="section-paragraph-s text-start mb-0">
                        Streamlined processes that reduce thetime and effort required to
                        handle service requests
                    </p>
                </div>
                <div class="d-flex align-items-start mb-3" style="gap: 8px">
                    <svg style="width: 16px; min-width: 16px" xmlns="http://www.w3.org/2000/svg" width="21" height="22"
                        viewBox="0 0 21 22" fill="none">
                        <path
                            d="M9.63867 14.2422C9.39258 14.4883 8.94141 14.4883 8.69531 14.2422L6.07031 11.6172C5.82422 11.3711 5.82422 10.9199 6.07031 10.6738C6.31641 10.4277 6.76758 10.4277 7.01367 10.6738L9.1875 12.8477L13.9453 8.04883C14.1914 7.80273 14.6426 7.80273 14.8887 8.04883C15.1348 8.29492 15.1348 8.74609 14.8887 8.99219L9.63867 14.2422ZM21 11.125C21 16.9492 16.2832 21.625 10.5 21.625C4.67578 21.625 0 16.9492 0 11.125C0 5.3418 4.67578 0.625 10.5 0.625C16.2832 0.625 21 5.3418 21 11.125ZM10.5 1.9375C5.41406 1.9375 1.3125 6.08008 1.3125 11.125C1.3125 16.2109 5.41406 20.3125 10.5 20.3125C15.5449 20.3125 19.6875 16.2109 19.6875 11.125C19.6875 6.08008 15.5449 1.9375 10.5 1.9375Z"
                            fill="#4B4B4B" />
                    </svg>
                    <p class="section-paragraph-s text-start mb-0">
                        Timely resolution of service requests thatresult in higher user
                        satisfaction
                    </p>
                </div>
                <div class="d-flex align-items-start mb-3" style="gap: 8px">
                    <svg style="width: 16px; min-width: 16px" xmlns="http://www.w3.org/2000/svg" width="21" height="22"
                        viewBox="0 0 21 22" fill="none">
                        <path
                            d="M9.63867 14.2422C9.39258 14.4883 8.94141 14.4883 8.69531 14.2422L6.07031 11.6172C5.82422 11.3711 5.82422 10.9199 6.07031 10.6738C6.31641 10.4277 6.76758 10.4277 7.01367 10.6738L9.1875 12.8477L13.9453 8.04883C14.1914 7.80273 14.6426 7.80273 14.8887 8.04883C15.1348 8.29492 15.1348 8.74609 14.8887 8.99219L9.63867 14.2422ZM21 11.125C21 16.9492 16.2832 21.625 10.5 21.625C4.67578 21.625 0 16.9492 0 11.125C0 5.3418 4.67578 0.625 10.5 0.625C16.2832 0.625 21 5.3418 21 11.125ZM10.5 1.9375C5.41406 1.9375 1.3125 6.08008 1.3125 11.125C1.3125 16.2109 5.41406 20.3125 10.5 20.3125C15.5449 20.3125 19.6875 16.2109 19.6875 11.125C19.6875 6.08008 15.5449 1.9375 10.5 1.9375Z"
                            fill="#4B4B4B" />
                    </svg>
                    <p class="section-paragraph-s text-start mb-0">
                        Increased transparency to keep users informed about the status
                        of requests
                    </p>
                </div>
                <div class="d-flex align-items-start mb-3" style="gap: 8px">
                    <svg style="width: 16px; min-width: 16px" xmlns="http://www.w3.org/2000/svg" width="21" height="22"
                        viewBox="0 0 21 22" fill="none">
                        <path
                            d="M9.63867 14.2422C9.39258 14.4883 8.94141 14.4883 8.69531 14.2422L6.07031 11.6172C5.82422 11.3711 5.82422 10.9199 6.07031 10.6738C6.31641 10.4277 6.76758 10.4277 7.01367 10.6738L9.1875 12.8477L13.9453 8.04883C14.1914 7.80273 14.6426 7.80273 14.8887 8.04883C15.1348 8.29492 15.1348 8.74609 14.8887 8.99219L9.63867 14.2422ZM21 11.125C21 16.9492 16.2832 21.625 10.5 21.625C4.67578 21.625 0 16.9492 0 11.125C0 5.3418 4.67578 0.625 10.5 0.625C16.2832 0.625 21 5.3418 21 11.125ZM10.5 1.9375C5.41406 1.9375 1.3125 6.08008 1.3125 11.125C1.3125 16.2109 5.41406 20.3125 10.5 20.3125C15.5449 20.3125 19.6875 16.2109 19.6875 11.125C19.6875 6.08008 15.5449 1.9375 10.5 1.9375Z"
                            fill="#4B4B4B" />
                    </svg>
                    <p class="section-paragraph-s text-start mb-0">
                        Prompt handling of service requests tominimize disruptions
                    </p>
                </div>
                <div class="d-flex align-items-start mb-3" style="gap: 8px">
                    <svg style="width: 16px; min-width: 16px" xmlns="http://www.w3.org/2000/svg" width="21" height="22"
                        viewBox="0 0 21 22" fill="none">
                        <path
                            d="M9.63867 14.2422C9.39258 14.4883 8.94141 14.4883 8.69531 14.2422L6.07031 11.6172C5.82422 11.3711 5.82422 10.9199 6.07031 10.6738C6.31641 10.4277 6.76758 10.4277 7.01367 10.6738L9.1875 12.8477L13.9453 8.04883C14.1914 7.80273 14.6426 7.80273 14.8887 8.04883C15.1348 8.29492 15.1348 8.74609 14.8887 8.99219L9.63867 14.2422ZM21 11.125C21 16.9492 16.2832 21.625 10.5 21.625C4.67578 21.625 0 16.9492 0 11.125C0 5.3418 4.67578 0.625 10.5 0.625C16.2832 0.625 21 5.3418 21 11.125ZM10.5 1.9375C5.41406 1.9375 1.3125 6.08008 1.3125 11.125C1.3125 16.2109 5.41406 20.3125 10.5 20.3125C15.5449 20.3125 19.6875 16.2109 19.6875 11.125C19.6875 6.08008 15.5449 1.9375 10.5 1.9375Z"
                            fill="#4B4B4B" />
                    </svg>
                    <p class="section-paragraph-s text-start mb-0">
                        Actionable insights with valuable data onrequest trends and
                        performance
                    </p>
                </div>
            </div>
        </div>
        <div class="col-xl-6 col-lg-6 col-md-12 col-sm-12 col-xs-12">
            <img src="/assets/img/feature-pages/infraon-itsm/service-request/service-request-feature-4.webp"
                class="w-100" style="box-shadow: none" alt="it asset management software" />
        </div>
    </div>
</section>

<section class="section-spacing bg-grey">
    <div class="container">
        <h2 class="section-title text-center">
            Discover Additional ITSM Features by Infraon
        </h2>

        <div class="row mx-0">
            <div class="col-xl-4 col-lg-4 col-md-12 col-sm-12 col-xs-12 p-4">
                <div class="backlink-container bg-white h-100" style="border: 1px solid #0000001c">
                    <a routerLink="/itsm-tool/features/sla-management-solution"
                        aria-label="Know more about SLA Management Solution">
                        <img class="feature-icon"
                            src="/assets/img/feature-pages/infraon-itsm/service-request/sla-mgmt.svg"
                            alt="SLA Management Solution icon" />
                        <p class="section-title-s text-start">
                            SLA Management Solution
                        </p>
                        <p>
                            Custom SLA templates handle escalations, improving
                            satisfaction and ensuring compliance requirements.
                        </p>
                    </a>
                </div>
            </div>
            <div class="col-xl-4 col-lg-4 col-md-12 col-sm-12 col-xs-12 p-4">
                <div class="backlink-container bg-white h-100" style="border: 1px solid #0000001c">
                    <a routerLink="/itsm-tool/features/event-management"
                        aria-label="Know more about Event Management">
                        <img class="feature-icon"
                            src="/assets/img/feature-pages/infraon-itsm/service-request/event-mgmt.svg"
                            alt="Event Management icon" />
                        <p class="section-title-s text-start">Event Management</p>
                        <p>
                            Real-time event detection and collection with API-based data
                            and service context.
                        </p>
                    </a>
                </div>
            </div>
            <div class="col-xl-4 col-lg-4 col-md-12 col-sm-12 col-xs-12 p-4">
                <div class="backlink-container bg-white h-100" style="border: 1px solid #0000001c">
                    <a routerLink="/itsm-tool/features/catalog-management"
                        aria-label="Know more about Service catalogue Management">
                        <img class="feature-icon"
                            src="/assets/img/feature-pages/infraon-itsm/service-request/service-catalogue-mgmt.svg"
                            alt="Service catalogue Management icon" />
                        <p class="section-title-s text-start">
                            Service catalogue Management
                        </p>
                        <p>
                            Centralized repository of available IT services for easy
                            request and fulfillment.
                        </p>
                    </a>
                </div>
            </div>
            <div class="col-xl-4 col-lg-4 col-md-12 col-sm-12 col-xs-12 p-4">
                <div class="backlink-container bg-white h-100" style="border: 1px solid #0000001c">
                    <a routerLink="/itsm-tool/features/service-request-management"
                        aria-label="Know more about Service Request Management">
                        <img class="feature-icon"
                            src="/assets/img/feature-pages/infraon-itsm/service-request/service-mgmt.svg"
                            alt="Service Request Management icon" />
                        <p class="section-title-s text-start">
                            Service Request Management
                        </p>
                        <p>
                            Streamlined handling of user requests with automated workflows
                            and efficient resource allocation.
                        </p>
                    </a>
                </div>
            </div>
            <div class="col-xl-4 col-lg-4 col-md-12 col-sm-12 col-xs-12 p-4">
                <div class="backlink-container bg-white h-100" style="border: 1px solid #0000001c">
                    <a routerLink="/itsm-tool/features/change-management-software"
                        aria-label="Know more about Change Management">
                        <img class="feature-icon"
                            src="/assets/img/feature-pages/infraon-itsm/service-request/change-mgmt.svg"
                            alt="Change Management icon" />
                        <p class="section-title-s text-start">Change Management</p>
                        <p>
                            Systematic handling of IT changes reduces risk and ensures
                            controlled service updates.
                        </p>
                    </a>
                </div>
            </div>
            <div class="col-xl-4 col-lg-4 col-md-12 col-sm-12 col-xs-12 p-4">
                <div class="backlink-container bg-white h-100" style="border: 1px solid #0000001c">
                    <a routerLink="/itsm-tool/features/workflow-automation"
                        aria-label="Know more about Workflow Automation">
                        <img class="feature-icon"
                            src="/assets/img/feature-pages/infraon-itsm/service-request/workflow-mgmt.svg"
                            alt="Workflow Automation icon" />
                        <p class="section-title-s text-start">Workflow Automation</p>
                        <p>
                            Custom automated workflows streamline processes and enable
                            business rule implementation.
                        </p>
                    </a>
                </div>
            </div>
        </div>

        <div class="row mx-0 justify-content-center pt-4">
            <a [routerLink]="['/sitemap']" [queryParams]="{ group: 'ITSM' }" class="primary-button">Checkout more features</a>
        </div>
    </div>
</section>

<section class="container section-spacing">
    <div id="gdpr-wrapper">
        <div class="gdpr-section justify-content-center">
            <img src="/assets/img/feature-pages/gdpr.webp" alt="GDPR Logo" class="me-4 w-100">
            <div>
                <h2 class="section-title mb-3">Our Product Suite is GDPR-Compliant</h2>
                <p class="section-paragraph mb-0">We are compliant with the European General Data Protection Regulation
                    (GDPR) – and <br> our SaaS-based product suite is fully committed to protecting the data of our
                    customers <br> as per the recent standards.</p>
            </div>
        </div>
    </div>
</section>


<section class="section-spacing bg-grey">
    <div class="container" id="g2-box-wrapper">
        <div class="g2-box justify-content-between">
            <img loading="lazy" src="/assets/img/feature-pages/g2.svg" alt="G2 Logo">
            <p>Already love using our products? Please <br> share your experiences on G2</p>
            <a href="https://www.g2.com/products/infraon-assets/reviews" class="primary-button">Rate us</a>
        </div>
    </div>
</section>